import { useCallback, useRef, useState, type RefObject } from "react";
import type { BoardSignalEvent } from "./useFramePresentation";

export type VoteOption = { id: string; label: string };

export type VoteBlock = {
  voteId: string;
  question: string;
  options: VoteOption[];
  /** peerId → optionId */
  tallies: Record<string, string>;
  myChoice: string | null;
  hostLabel: string | null;
  /** Кто создал: peerId в текущей сессии */
  hostPeerId: string | null;
  /** Стабильный user id (после reload создатель снова может закрыть) */
  hostUserId: string | null;
  blockIndex: number;
  worldX: number;
  worldY: number;
  widthPx: number;
  heightPx: number;
};

type PresenceSignal = {
  myPeerId?: string | null;
  sendSignal?: (kind: string, payload?: Record<string, unknown>) => boolean;
} | null;

const DEFAULT_OPTIONS: VoteOption[] = [
  { id: "yes", label: "Да" },
  { id: "no", label: "Нет" },
  { id: "idk", label: "Не уверен" },
];

/** Сколько вариантов можно в опросе и какой длины. */
export const VOTE_OPTIONS_MIN = 2;
export const VOTE_OPTIONS_MAX = 8;
export const VOTE_OPTION_LABEL_MAX = 60;

/** id нового варианта — уникальный внутри опроса. */
export function newVoteOptionId(): string {
  return `o-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Варианты без пустых и повторных id, в пределах лимитов. */
function normalizeOptions(raw: VoteOption[]): VoteOption[] {
  const seen = new Set<string>();
  const out: VoteOption[] = [];
  for (const o of raw) {
    if (!o.id || seen.has(o.id)) continue;
    seen.add(o.id);
    out.push({ id: o.id, label: o.label.trim().slice(0, VOTE_OPTION_LABEL_MAX) || "Вариант" });
    if (out.length >= VOTE_OPTIONS_MAX) break;
  }
  return out;
}

/** Новые варианты: голоса за удалённые снимаются, остальные остаются. */
function withOptions(b: VoteBlock, options: VoteOption[]): VoteBlock {
  const ids = new Set(options.map((o) => o.id));
  const tallies: Record<string, string> = {};
  for (const [peer, choice] of Object.entries(b.tallies)) {
    if (ids.has(choice)) tallies[peer] = choice;
  }
  return {
    ...b,
    options,
    tallies,
    myChoice: b.myChoice && ids.has(b.myChoice) ? b.myChoice : null,
  };
}

export const VOTE_WIDTH_MIN = 220;
export const VOTE_WIDTH_MAX = 480;
export const VOTE_HEIGHT_MIN = 220;
export const VOTE_HEIGHT_MAX = 560;
type StartOpts = { question?: string; worldX?: number; worldY?: number };

export const VOTE_WIDTH_DEFAULT = 280;
export const VOTE_HEIGHT_DEFAULT = 340;

const LOCAL_HOST_VOTES_KEY = "wb-vote-host-ids";

function readLocalHostVotes(): Set<string> {
  try {
    const raw = sessionStorage.getItem(LOCAL_HOST_VOTES_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function rememberLocalHostVote(voteId: string) {
  try {
    const set = readLocalHostVotes();
    set.add(voteId);
    sessionStorage.setItem(LOCAL_HOST_VOTES_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

function clampSize(w: number, h: number) {
  return {
    widthPx: Math.min(VOTE_WIDTH_MAX, Math.max(VOTE_WIDTH_MIN, Math.round(w))),
    heightPx: Math.min(VOTE_HEIGHT_MAX, Math.max(VOTE_HEIGHT_MIN, Math.round(h))),
  };
}

function parseOptions(raw: unknown): VoteOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) => {
      if (!o || typeof o !== "object") return null;
      const r = o as Record<string, unknown>;
      if (typeof r.id !== "string" || typeof r.label !== "string") return null;
      return { id: r.id, label: r.label.slice(0, VOTE_OPTION_LABEL_MAX) };
    })
    .filter((x): x is VoteOption => Boolean(x));
}

function parseWorld(payload: Record<string, unknown>, fallbackIndex = 0) {
  const x =
    typeof payload.worldX === "number" && Number.isFinite(payload.worldX)
      ? payload.worldX
      : 120 + fallbackIndex * 40;
  const y =
    typeof payload.worldY === "number" && Number.isFinite(payload.worldY)
      ? payload.worldY
      : 80 + fallbackIndex * 40;
  return { worldX: x, worldY: y };
}

function parseSize(payload: Record<string, unknown>) {
  const w =
    typeof payload.widthPx === "number" && Number.isFinite(payload.widthPx)
      ? payload.widthPx
      : VOTE_WIDTH_DEFAULT;
  const h =
    typeof payload.heightPx === "number" && Number.isFinite(payload.heightPx)
      ? payload.heightPx
      : VOTE_HEIGHT_DEFAULT;
  return clampSize(w, h);
}

function parseTallies(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof k === "string" && typeof v === "string") out[k] = v;
  }
  return out;
}

function parseHostIds(payload: Record<string, unknown>, fallbackPeerId: string | null) {
  const hostPeerId =
    typeof payload.hostPeerId === "string" && payload.hostPeerId
      ? payload.hostPeerId
      : fallbackPeerId;
  const hostUserId =
    typeof payload.hostUserId === "string" && payload.hostUserId
      ? payload.hostUserId
      : null;
  return { hostPeerId, hostUserId };
}

function serializeBlocks(blocks: VoteBlock[]) {
  return blocks.map((b) => ({
    voteId: b.voteId,
    question: b.question,
    options: b.options,
    tallies: b.tallies,
    blockIndex: b.blockIndex,
    worldX: b.worldX,
    worldY: b.worldY,
    widthPx: b.widthPx,
    heightPx: b.heightPx,
    hostLabel: b.hostLabel,
    hostPeerId: b.hostPeerId,
    hostUserId: b.hostUserId,
  }));
}

function parseSyncBlock(
  raw: unknown,
  myPeerId: string | null
): VoteBlock | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const voteId = typeof r.voteId === "string" ? r.voteId : null;
  const options = parseOptions(r.options);
  if (!voteId || options.length < 2) return null;
  const blockIndex =
    typeof r.blockIndex === "number" && Number.isFinite(r.blockIndex)
      ? Math.max(1, Math.floor(r.blockIndex))
      : 1;
  const question =
    typeof r.question === "string" && r.question.trim()
      ? r.question.trim().slice(0, 120)
      : `Опрос ${blockIndex}`;
  const { worldX, worldY } = parseWorld(r, blockIndex);
  const { widthPx, heightPx } = parseSize(r);
  const tallies = parseTallies(r.tallies);
  const myChoice =
    myPeerId && tallies[myPeerId] ? tallies[myPeerId] : null;
  const { hostPeerId, hostUserId } = parseHostIds(r, null);
  return {
    voteId,
    question,
    options,
    tallies,
    myChoice,
    hostLabel: typeof r.hostLabel === "string" ? r.hostLabel : null,
    hostPeerId,
    hostUserId,
    blockIndex,
    worldX,
    worldY,
    widthPx,
    heightPx,
  };
}

export function useBoardVote(opts: {
  presenceRef: RefObject<PresenceSignal>;
  canHost: boolean;
  /** Стабильный id аккаунта — чтобы после F5 создатель снова мог закрыть опрос */
  myUserId?: string | null;
}) {
  const { presenceRef, canHost, myUserId = null } = opts;
  const myUserIdRef = useRef(myUserId);
  myUserIdRef.current = myUserId;
  const [blocks, setBlocks] = useState<VoteBlock[]>([]);
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const nextIndexRef = useRef(1);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOwner = useCallback(
    (block: VoteBlock) => {
      const myPeer = presenceRef.current?.myPeerId ?? null;
      const uid = myUserIdRef.current;
      if (uid && block.hostUserId && uid === block.hostUserId) return true;
      if (myPeer && block.hostPeerId && myPeer === block.hostPeerId) return true;
      // Гость без userId: помним свои опросы в sessionStorage после reload
      if (readLocalHostVotes().has(block.voteId)) return true;
      return false;
    },
    [presenceRef]
  );

  const isOwnerOf = useCallback(
    (voteId: string) => {
      const b = blocksRef.current.find((x) => x.voteId === voteId);
      return b ? isOwner(b) : false;
    },
    [isOwner]
  );

  const sendSync = useCallback(() => {
    const cur = blocksRef.current;
    if (!cur.length) return;
    presenceRef.current?.sendSignal?.("vote_sync", {
      blocks: serializeBlocks(cur),
    });
  }, [presenceRef]);

  const onPeerJoined = useCallback(() => {
    if (!blocksRef.current.length) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      syncTimerRef.current = null;
      sendSync();
    }, 80 + Math.floor(Math.random() * 200));
  }, [sendSync]);

  /**
   * Опрос, который попросили поставить, пока доска была без связи. Раньше
   * кнопка в этот момент молча ничего не делала — казалось, что опрос
   * «не выбирается». Теперь он встаёт сам, как только связь появится.
   */
  const pendingStartRef = useRef<StartOpts | null>(null);
  const startRef = useRef<((o?: StartOpts) => boolean) | null>(null);

  const onSubscribed = useCallback(() => {
    presenceRef.current?.sendSignal?.("vote_sync_request", {});
    if (blocksRef.current.length) sendSync();
    const pending = pendingStartRef.current;
    if (pending) {
      pendingStartRef.current = null;
      // Сервер пропускает сигналы не чаще раза в 80 мс — запрос сверки
      // только что ушёл, опрос отправляем следом.
      window.setTimeout(() => startRef.current?.(pending), 150);
    }
  }, [presenceRef, sendSync]);

  const mergeSyncBlocks = useCallback((incoming: VoteBlock[]) => {
    if (!incoming.length) return;
    setBlocks((prev) => {
      const byId = new Map(prev.map((b) => [b.voteId, b]));
      for (const b of incoming) {
        const existing = byId.get(b.voteId);
        if (!existing) {
          byId.set(b.voteId, b);
          continue;
        }
        byId.set(b.voteId, {
          ...existing,
          question: b.question,
          options: b.options,
          tallies: { ...existing.tallies, ...b.tallies },
          myChoice: b.myChoice ?? existing.myChoice,
          blockIndex: b.blockIndex,
          worldX: b.worldX,
          worldY: b.worldY,
          widthPx: b.widthPx,
          heightPx: b.heightPx,
          hostLabel: b.hostLabel ?? existing.hostLabel,
          hostPeerId: b.hostPeerId ?? existing.hostPeerId,
          hostUserId: b.hostUserId ?? existing.hostUserId,
        });
      }
      const next = [...byId.values()].sort((a, c) => a.blockIndex - c.blockIndex);
      const maxIdx = next.reduce((m, b) => Math.max(m, b.blockIndex), 0);
      nextIndexRef.current = Math.max(nextIndexRef.current, maxIdx + 1);
      return next;
    });
  }, []);

  const handleSignal = useCallback(
    (ev: BoardSignalEvent) => {
      if (ev.kind === "vote_sync_request") {
        if (ev.isSelf) return;
        if (blocksRef.current.length) sendSync();
        return;
      }

      if (ev.kind === "vote_sync") {
        if (ev.isSelf) return;
        const raw = Array.isArray(ev.payload.blocks) ? ev.payload.blocks : [];
        const myPeerId = presenceRef.current?.myPeerId ?? null;
        const parsed = raw
          .map((row) => parseSyncBlock(row, myPeerId))
          .filter((x): x is VoteBlock => Boolean(x));
        mergeSyncBlocks(parsed);
        return;
      }

      if (ev.kind === "vote_start") {
        const voteId = typeof ev.payload.voteId === "string" ? ev.payload.voteId : null;
        const question =
          typeof ev.payload.question === "string" && ev.payload.question.trim()
            ? ev.payload.question.trim().slice(0, 120)
            : "Опрос";
        const options = parseOptions(ev.payload.options);
        if (!voteId || options.length < 2) return;
        const blockIndex =
          typeof ev.payload.blockIndex === "number" && Number.isFinite(ev.payload.blockIndex)
            ? Math.max(1, Math.floor(ev.payload.blockIndex))
            : nextIndexRef.current;
        nextIndexRef.current = Math.max(nextIndexRef.current, blockIndex + 1);
        const { worldX, worldY } = parseWorld(ev.payload, blockIndex);
        const { widthPx, heightPx } = parseSize(ev.payload);
        const { hostPeerId, hostUserId } = parseHostIds(
          ev.payload,
          ev.peerId || (ev.isSelf ? presenceRef.current?.myPeerId ?? null : null)
        );
        setBlocks((prev) => {
          if (prev.some((b) => b.voteId === voteId)) return prev;
          return [
            ...prev,
            {
              voteId,
              question,
              options,
              tallies: {},
              myChoice: null,
              hostLabel: ev.displayLabel,
              hostPeerId,
              hostUserId,
              blockIndex,
              worldX,
              worldY,
              widthPx,
              heightPx,
            },
          ];
        });
        return;
      }

      // Автор поменял варианты: переименовал, добавил или убрал.
      if (ev.kind === "vote_options") {
        const voteId = typeof ev.payload.voteId === "string" ? ev.payload.voteId : null;
        const options = normalizeOptions(parseOptions(ev.payload.options));
        if (!voteId || options.length < VOTE_OPTIONS_MIN) return;
        setBlocks((prev) => prev.map((b) => (b.voteId === voteId ? withOptions(b, options) : b)));
        return;
      }

      if (ev.kind === "vote_title") {
        const voteId = typeof ev.payload.voteId === "string" ? ev.payload.voteId : null;
        const question =
          typeof ev.payload.question === "string" ? ev.payload.question.trim().slice(0, 120) : "";
        if (!voteId || !question) return;
        setBlocks((prev) =>
          prev.map((b) => (b.voteId === voteId ? { ...b, question } : b))
        );
        return;
      }

      if (ev.kind === "vote_move") {
        if (ev.isSelf) return;
        const voteId = typeof ev.payload.voteId === "string" ? ev.payload.voteId : null;
        if (!voteId) return;
        const { worldX, worldY } = parseWorld(ev.payload);
        setBlocks((prev) =>
          prev.map((b) => (b.voteId === voteId ? { ...b, worldX, worldY } : b))
        );
        return;
      }

      if (ev.kind === "vote_resize") {
        if (ev.isSelf) return;
        const voteId = typeof ev.payload.voteId === "string" ? ev.payload.voteId : null;
        if (!voteId) return;
        const { widthPx, heightPx } = parseSize(ev.payload);
        setBlocks((prev) =>
          prev.map((b) => (b.voteId === voteId ? { ...b, widthPx, heightPx } : b))
        );
        return;
      }

      if (ev.kind === "vote_cast") {
        const voteId = typeof ev.payload.voteId === "string" ? ev.payload.voteId : null;
        const optionId = typeof ev.payload.optionId === "string" ? ev.payload.optionId : null;
        const voterId = ev.peerId || (ev.isSelf ? "self" : null);
        if (!voteId || !optionId || !voterId) return;
        setBlocks((prev) =>
          prev.map((b) => {
            if (b.voteId !== voteId) return b;
            if (!b.options.some((o) => o.id === optionId)) return b;
            return {
              ...b,
              tallies: { ...b.tallies, [voterId]: optionId },
              myChoice: ev.isSelf ? optionId : b.myChoice,
            };
          })
        );
        return;
      }

      if (ev.kind === "vote_end") {
        const voteId = typeof ev.payload.voteId === "string" ? ev.payload.voteId : null;
        if (!voteId) {
          setBlocks([]);
          return;
        }
        setBlocks((prev) => prev.filter((b) => b.voteId !== voteId));
      }
    },
    [mergeSyncBlocks, presenceRef, sendSync]
  );

  const start = useCallback(
    (startOpts?: StartOpts) => {
      if (!canHost) return false;
      const voteId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `vote-${Date.now()}`;
      const blockIndex = nextIndexRef.current++;
      const q =
        (startOpts?.question ?? `Опрос ${blockIndex}`).trim().slice(0, 120) ||
        `Опрос ${blockIndex}`;
      const worldX =
        typeof startOpts?.worldX === "number" && Number.isFinite(startOpts.worldX)
          ? startOpts.worldX
          : 120 + (blockIndex - 1) * 40;
      const worldY =
        typeof startOpts?.worldY === "number" && Number.isFinite(startOpts.worldY)
          ? startOpts.worldY
          : 80 + (blockIndex - 1) * 40;
      const widthPx = VOTE_WIDTH_DEFAULT;
      const heightPx = VOTE_HEIGHT_DEFAULT;
      const options = DEFAULT_OPTIONS;
      const hostPeerId = presenceRef.current?.myPeerId ?? null;
      const hostUserId = myUserIdRef.current;
      const ok = presenceRef.current?.sendSignal?.("vote_start", {
        voteId,
        question: q,
        options,
        blockIndex,
        worldX,
        worldY,
        widthPx,
        heightPx,
        hostPeerId,
        hostUserId,
      });
      if (!ok) return false;
      rememberLocalHostVote(voteId);
      setBlocks((prev) => [
        ...prev,
        {
          voteId,
          question: q,
          options,
          tallies: {},
          myChoice: null,
          hostLabel: null,
          hostPeerId,
          hostUserId,
          blockIndex,
          worldX,
          worldY,
          widthPx,
          heightPx,
        },
      ]);
      return true;
    },
    [canHost, presenceRef]
  );

  const cast = useCallback(
    (voteId: string, optionId: string) => {
      const cur = blocksRef.current.find((b) => b.voteId === voteId);
      if (!cur) return;
      if (!cur.options.some((o) => o.id === optionId)) return;
      presenceRef.current?.sendSignal?.("vote_cast", { voteId, optionId });
      const selfId = presenceRef.current?.myPeerId || "self";
      setBlocks((prev) =>
        prev.map((b) =>
          b.voteId === voteId
            ? {
                ...b,
                myChoice: optionId,
                tallies: { ...b.tallies, [selfId]: optionId },
              }
            : b
        )
      );
    },
    [presenceRef]
  );

  const setTitle = useCallback(
    (voteId: string, question: string) => {
      const cur = blocksRef.current.find((b) => b.voteId === voteId);
      if (!cur || !isOwner(cur)) return;
      const q = question.trim().slice(0, 120) || `Опрос ${cur.blockIndex}`;
      presenceRef.current?.sendSignal?.("vote_title", { voteId, question: q });
      setBlocks((prev) =>
        prev.map((b) => (b.voteId === voteId ? { ...b, question: q } : b))
      );
    },
    [isOwner, presenceRef]
  );

  /** Варианты ответа — только у автора опроса. */
  const setOptions = useCallback(
    (voteId: string, next: VoteOption[]) => {
      const cur = blocksRef.current.find((b) => b.voteId === voteId);
      if (!cur || !isOwner(cur)) return;
      const options = normalizeOptions(next);
      if (options.length < VOTE_OPTIONS_MIN) return;
      setBlocks((prev) => prev.map((b) => (b.voteId === voteId ? withOptions(b, options) : b)));
      presenceRef.current?.sendSignal?.("vote_options", { voteId, options });
    },
    [isOwner, presenceRef]
  );

  const move = useCallback(
    (voteId: string, worldX: number, worldY: number) => {
      if (!blocksRef.current.some((b) => b.voteId === voteId)) return;
      if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) return;
      setBlocks((prev) =>
        prev.map((b) => (b.voteId === voteId ? { ...b, worldX, worldY } : b))
      );
      presenceRef.current?.sendSignal?.("vote_move", { voteId, worldX, worldY });
    },
    [presenceRef]
  );

  const resize = useCallback(
    (voteId: string, widthPx: number, heightPx: number) => {
      if (!blocksRef.current.some((b) => b.voteId === voteId)) return;
      const size = clampSize(widthPx, heightPx);
      setBlocks((prev) =>
        prev.map((b) => (b.voteId === voteId ? { ...b, ...size } : b))
      );
      presenceRef.current?.sendSignal?.("vote_resize", {
        voteId,
        widthPx: size.widthPx,
        heightPx: size.heightPx,
      });
    },
    [presenceRef]
  );

  const end = useCallback(
    (voteId: string) => {
      const cur = blocksRef.current.find((b) => b.voteId === voteId);
      if (!cur || !isOwner(cur)) return;
      presenceRef.current?.sendSignal?.("vote_end", { voteId });
      setBlocks((prev) => prev.filter((b) => b.voteId !== voteId));
    },
    [isOwner, presenceRef]
  );

  const countsFor = useCallback((voteId: string) => {
    const cur = blocksRef.current.find((b) => b.voteId === voteId);
    const map: Record<string, number> = {};
    if (!cur) return map;
    for (const o of cur.options) map[o.id] = 0;
    for (const optId of Object.values(cur.tallies)) {
      if (map[optId] != null) map[optId] += 1;
    }
    return map;
  }, []);

  startRef.current = start;

  /** Поставить опрос, а без связи — поставить, как только она появится. */
  const startOrQueue = useCallback(
    (startOpts?: StartOpts): "started" | "queued" | "denied" => {
      if (!canHost) return "denied";
      if (start(startOpts)) return "started";
      pendingStartRef.current = startOpts ?? {};
      return "queued";
    },
    [canHost, start]
  );

  return {
    blocks,
    active: blocks.length > 0,
    isOwnerOf,
    start,
    startOrQueue,
    cast,
    end,
    move,
    resize,
    setTitle,
    setOptions,
    countsFor,
    handleSignal,
    onPeerJoined,
    onSubscribed,
  };
}
